import type { CaptureEdgeKind } from '@vivport/backend/types';

export const CAPTURE_MODES = ['recording', 'photo', 'video'] as const;
export type CaptureMode = (typeof CAPTURE_MODES)[number];

const PANEL_TITLE = 'Observation Capture';
const photoCount = (count: number) => `${count} photo${count === 1 ? '' : 's'}`;
const recordingCount = (count: number) => `${count} recording${count === 1 ? '' : 's'}`;
const videoCount = (count: number) => `${count} video${count === 1 ? '' : 's'}`;
const edgeWord = (edgeKind: CaptureEdgeKind | null) =>
  edgeKind === 'ATTACHMENT' ? 'attached to' : 'linked to';

export const OBSERVATION_CAPTURE_LABELS = {
  panelTitle: PANEL_TITLE,
  modes: {
    recording: 'Audio',
    photo: 'Photo',
    video: 'Video',
  },
  modeHint: (media: string) => `Record or upload ${media}, grouping with other multimedia`,
  modeHintGroupPrefix: (media: string) => `Record or upload ${media}, group into`,
  observationNew: 'New',
  observationSelectLabel: 'Group into',
  modeMedia: {
    recording: 'audio',
    photo: 'photos',
    video: 'video',
  },
  addUnlinked: 'Add Unlinked',
  addLinked: 'Add Linked',
  linkedTo: 'Linked to',
  linkClear: 'Clear link',
  linkLoading: 'Loading…',
  edgeWord,
  staged: {
    submit: (parts: string[]) => `Submit ${parts.join(' and ')}`,
    submitEmpty: 'Add a photo, a recording or a video to submit',
    remove: (name: string) => `Remove ${name}`,
    submitFailed: 'Submit failed. Please try again.',
    recordFromHere: 'Record from here',
    splicing: 'Merging…',
    spliceFailed: 'Could not merge that recording. The original is unchanged.',
    punchHint: 'Play to where you want to start again, then Record from here — everything after that point is replaced.',
  },
  session: {
    heading: 'Added so far',
    done: 'Done',
    feedOpen: 'Observation Capture is open in the composer.',
    feedIdle: 'Observation Capture is ready whenever you are.',
    feedReopen: 'Show',
    photoCount,
    recordingCount,
    videoCount,
    captureSummary: (parts: string[]) => `Added ${parts.join(' and ')} via ${PANEL_TITLE}.`,
    summary: (parts: string[]) => `Added ${parts.join(' and ')} · one observation`,
    summaryLinked: (parts: string[], edgeKind: CaptureEdgeKind | null, linkTitle: string) =>
      `Added ${parts.join(' and ')} · one observation, ${edgeWord(edgeKind)} ${linkTitle}`,
    settledPhoto: 'Photo added',
    settledRecording: 'Recording added',
    settledVideo: 'Video added',
    settledLinked: (noun: string, edgeKind: CaptureEdgeKind | null, linkTitle: string) =>
      `${noun} and ${edgeWord(edgeKind)} ${linkTitle}`,
    itemFallback: (contentId: number) => `#${contentId}`,
  },
  recording: {
    record: 'Record',
    pause: 'Pause',
    resume: 'Resume',
    stop: 'Stop',
    upload: 'Upload audio',
    rewind: 'Rewind 5s',
    inProgress: 'Recording in progress',
    pausedStatus: 'Recording paused',
  },
  photo: {
    upload: 'Upload photos',
    take: 'Take photo',
    close: 'Close camera',
    capture: 'Capture',
    cameraError: 'Camera unavailable. Check permissions or use Upload photos instead.',
  },
  video: {
    record: 'Record video',
    start: 'Record',
    pause: 'Pause',
    resume: 'Resume',
    stop: 'Stop',
    close: 'Close camera',
    upload: 'Upload video',
    elapsed: (seconds: number) =>
      `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`,
    cameraError: 'Camera unavailable. Check permissions or use Upload video instead.',
  },
} as const;
